import { useEffect, useState, useRef } from "react";
import { ProductCard } from "@/components/product/ProductCard";
import {
  getProducts,
  getRelatedProducts,
  searchProducts,
} from "@/services/productService";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Loader2, Plus, ShoppingCart } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { SafeImage } from "@/components/common/SafeImage";
import { resolveProductImages } from "@/utils/media";
import { formatPrice } from "@/services/pricingService";
import { useAuth } from "@/context/AuthContext";
import { getDisplayPrice } from "@/services/pricingService";
import { useVehicleSelection } from "@/context/VehicleContext";
import { getProductCompatibility } from "@/services/productService";

export function RelatedProducts({ currentProduct, limit = 6 }) {
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isLoadingRelated, setIsLoadingRelated] = useState(false);
  const searchRef = useRef(null);
  const { addToCart } = useCart();
  const { user } = useAuth();
  const { selection, isActive: isVehicleSelectionActive } = useVehicleSelection();

  useEffect(() => {
    const load = async () => {
      if (!currentProduct?._id) return;
      setIsLoadingRelated(true);
      try {
        const selectedVehicleIds = Array.isArray(selection?.vehicleIds)
          ? selection.vehicleIds.map((id) => String(id)).filter(Boolean)
          : [];
        const hasExplicitVehicleContext = isVehicleSelectionActive && selectedVehicleIds.length > 0;

        const [fallbackRelated, compatibility] = await Promise.all([
          getRelatedProducts(currentProduct._id, limit * 2),
          hasExplicitVehicleContext
            ? Promise.resolve(null)
            : getProductCompatibility(currentProduct._id).catch(() => null),
        ]);

        const compatibilityVehicleIds = hasExplicitVehicleContext
          ? selectedVehicleIds
          : Array.from(
              new Set(
                Object.values(compatibility || {})
                  .flatMap((list) => (Array.isArray(list) ? list : []))
                  .map((item) => String(item?._id || ""))
                  .filter(Boolean),
              ),
            );

        if (compatibilityVehicleIds.length > 0) {
          const compatible = await getProducts({
            vehicleIds: compatibilityVehicleIds,
            page: 1,
            limit: limit * 2,
          });

          const compatibleItems = Array.isArray(compatible)
            ? compatible
            : Array.isArray(compatible?.items)
              ? compatible.items
              : [];

          const merged = [];
          const seen = new Set();
          const pushUnique = (item) => {
            const id = String(item?._id || item?.id || "");
            if (!id || id === String(currentProduct._id) || seen.has(id)) return;
            seen.add(id);
            merged.push(item);
          };

          compatibleItems.forEach(pushUnique);
          (Array.isArray(fallbackRelated) ? fallbackRelated : []).forEach(pushUnique);
          setRelatedProducts(merged.slice(0, limit));
          return;
        }

        setRelatedProducts(Array.isArray(fallbackRelated) ? fallbackRelated.slice(0, limit) : []);
      } finally {
        setIsLoadingRelated(false);
      }
    };
    load();
  }, [
    currentProduct?._id,
    limit,
    selection?.vehicleIds,
    isVehicleSelectionActive,
  ]);

  // Handle Search Fetching
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchQuery.trim().length > 1) {
        setIsSearching(true);
        setShowResults(true);
        try {
          const results = await searchProducts(searchQuery, { limit: 5 });
          setSearchResults(results);
        } catch (error) {
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  // Handle Click Outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleQuickAdd = async (product) => {
    const added = await addToCart(product, 1);
    if (added) {
      toast.success(`Added ${product.name} to cart`);
      setShowResults(false);
      setSearchQuery("");
    }
  };

  if (!currentProduct) return null;

  return (
    <section className="pt-8 border-t border-zinc-200">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <h2 className="text-2xl font-bold">You May Also Like</h2>
        
        {/* Quick Search & Add */}
        <div className="relative w-full md:w-80" ref={searchRef}>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
            <Input
              placeholder="Search parts, brands, OEM numbers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-11 rounded-full bg-secondary/40 border-transparent focus:bg-background transition-all placeholder:text-muted-foreground/50"
            />
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>

          {/* Quick Results Dropdown */}
          {showResults && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-background border border-border rounded-xl shadow-xl z-30 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
              {searchResults.length > 0 ? (
                <div className="max-h-64 overflow-y-auto">
                  {searchResults.map((product) => (
                    <div
                      key={product._id}
                      className="flex items-center gap-3 p-2 hover:bg-secondary/50 transition-colors border-b border-border/50 last:border-0"
                    >
                      <div className="relative h-12 w-12 rounded-lg overflow-hidden bg-white border border-border/50">
                        <SafeImage
                          src={resolveProductImages(product.images || [])[0] || "/placeholder.jpg"}
                          alt={product.name}
                          fill
                          className="object-contain p-1"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold truncate">{product.name}</p>
                        <p className="text-[10px] text-primary font-bold">
                          {formatPrice(getDisplayPrice(product, user).price)}
                        </p>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 rounded-full hover:bg-primary hover:text-white"
                        onClick={() => handleQuickAdd(product)}
                      >
                        <ShoppingCart className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : !isSearching && searchQuery.length > 1 ? (
                <div className="p-4 text-center text-xs text-muted-foreground">
                  No products found
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {/* Mobile: Horizontal scroll */}
      {isLoadingRelated ? (
        <div className="rounded-xl border border-dashed border-border bg-card/50 px-4 py-8 text-center text-sm text-muted-foreground">
          Loading vehicle-matched recommendations...
        </div>
      ) : relatedProducts.length > 0 ? (
        <>
          <div className="md:hidden overflow-x-auto -mx-4 px-4">
            <div className="flex gap-4 pb-4">
              {relatedProducts.map((product) => (
                <div key={product._id || product.id} className="w-64 flex-shrink-0">
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          </div>

          <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {relatedProducts.map((product) => (
              <ProductCard key={product._id || product.id} product={product} />
            ))}
          </div>
        </>
      ) : (
        <div className="rounded-xl border border-dashed border-border bg-card/50 px-4 py-8 text-center text-sm text-muted-foreground">
          No compatible related products found for the selected vehicle.
        </div>
      )}
    </section>
  );
}
